import React, { useState, useRef } from 'react';
import { DB, uploadImage } from '../data/database';
import { ROLES, CURRENCIES } from '../data/constants';
import { convertCurrency } from '../utils/currency';
import CurrencySelect from './CurrencySelect';
import { logActivity, Notifications } from '../data/notifications';
import { CarIcon, EngineIcon, PartsIcon, CheckIcon, ContainerIcon } from './Icons';
import centralUpdateStatus, { detectUnitType } from '../utils/updateUnitStatus';
import generateBrandedQR from '../utils/generateBrandedQR';
import PrintLabelModal from './PrintLabelModal';

// ── Helpers ──────────────────────────────────────────────────
function lookupUnit(sku) {
  const cleaned = sku.replace(/^(BIGSTAR:|https?:\/\/[^?]*\?u(?:nit)?=)/i, '').trim().toUpperCase();
  const tables = [
    { key: 'vehicles',   items: DB.get('vehicles') || [] },
    { key: 'engines',    items: DB.get('engines') || [] },
    { key: 'spareParts', items: DB.get('spareParts') || [] },
  ];
  for (const t of tables) {
    const found = t.items.find(u => u.sku?.toUpperCase() === cleaned);
    if (found) return { ...found, _dbKey: t.key };
  }
  return null;
}

function getUnitName(u) { return u.make ? `${u.make} ${u.model || ''}`.trim() : u.engineName || u.partName || 'Unknown'; }
function getUnitIcon(u) { return u.make ? <CarIcon /> : u.engineName ? <EngineIcon /> : <PartsIcon />; }
function getDbKey(u) { return u._dbKey || (u.make ? 'vehicles' : u.engineName ? 'engines' : 'spareParts'); }

function saveUnit(unit, updates) {
  const key = getDbKey(unit);
  const list = DB.get(key) || [];
  DB.set(key, list.map(u => u.id === unit.id ? { ...u, ...updates } : u));
}

// Compress & upload image helper
async function compressAndUpload(file) {
  if (!file || !file.type.startsWith('image/')) return '';
  const compressed = await new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1200;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
          else { width = Math.round(width * MAX / height); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.75));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
  return await uploadImage(compressed);
}

// ── YARD LOCATIONS ───────────────────────────────────────────
const YARD_LOCATIONS = [
  'Japan Yard', 'Sharjah Yard',
  'Yokohama Yard A', 'Yokohama Yard B', 'Nagoya Yard', 'Kobe Yard', 'Osaka Yard',
];

// ── STATUS TRANSITIONS ──────────────────────────────────────
const YARD_STATUS_TRANSITIONS = {
  japan_yard: ['available'],
  available: ['loading', 'cutting'],
  loading: ['loaded'],
  cutting: ['dismantled'],
};
const ALL_STATUSES = ['japan_yard','available','assigned','loading','loaded','shipped','in_transit','at_port','customs','delivered','sold','cutting','dismantled'];
const ARRIVAL_STATUSES = ['shipped','in_transit','at_port','customs'];

const CONDITIONS = [
  { id: 'good', label: 'Good', icon: '✅', color: 'var(--green)' },
  { id: 'minor_damage', label: 'Minor Damage', icon: '⚠️', color: 'var(--yellow)' },
  { id: 'major_damage', label: 'Major Damage', icon: '🔴', color: 'var(--red)' },
  { id: 'missing_parts', label: 'Missing Parts', icon: '❌', color: 'var(--orange, var(--yellow))' },
];

// ══════════════════════════════════════════════════════════════
// QRActionMenu — shown after scanning/finding a unit
// ══════════════════════════════════════════════════════════════
function QRActionMenu({ scannedValue, user, onClose, onNavigate }) {
  const [unit, setUnit] = useState(() => lookupUnit(scannedValue));
  const [action, setAction] = useState(null); // current sub-action
  const [toast, setToast]   = useState('');

  const role = ROLES[user.role] || {};
  const isAdmin = user.role === 'head_admin' || user.role === 'admin';
  const isYard = user.role === 'japan_yard';
  const isYardSales = user.role === 'yard_sales';
  const isAccountant = user.role === 'accountant';
  const isCustomer = role.isCustomer;
  const isDestination = user.role === 'destination';

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const refreshUnit = () => {
    const fresh = lookupUnit(unit?.sku || scannedValue);
    if (fresh) setUnit(fresh);
  };

  // ── NOT FOUND ──
  if (!unit) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">Unit Not Found</h3>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="modal-body" style={{ textAlign: 'center', padding: '24px 20px' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>❌</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Unit Not Found</div>
            <div style={{ fontSize: 12, color: 'var(--cyan)', fontFamily: 'monospace', marginBottom: 12 }}>
              SKU: {scannedValue}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>
              This QR code doesn't match any unit in the system.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button className="btn btn-primary" onClick={onClose}>Scan Again</button>
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const customers = DB.get('customers') || [];
  const custName = unit.assignedTo ? (customers.find(c => c.id === unit.assignedTo)?.name || 'Unknown') : null;
  const containers = DB.get('containers') || [];
  const unitContainer = containers.find(c => (c.units || []).includes(unit.id));

  // ── CUSTOMER OWNERSHIP CHECK ──
  // If a customer scans a unit not assigned to them, block access
  if (isCustomer && unit.assignedTo !== user.id) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">Not Your Unit</h3>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="modal-body" style={{ textAlign: 'center', padding: '24px 20px' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>This unit is not in your account</div>
            <div style={{ fontSize: 12, color: 'var(--cyan)', fontFamily: 'monospace', marginBottom: 12 }}>{unit.sku}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>
              You can only interact with units assigned to your account.
            </div>
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  // ── YARD SALES: block non-Sharjah units ──
  if (isYardSales && unit.yard_location !== 'sharjah_yard') {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">Not Available</h3>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="modal-body" style={{ textAlign: 'center', padding: '24px 20px' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🇯🇵</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>This unit is in {unit.yard_location === 'japan_yard' ? 'Japan Yard' : (unit.yard_location || 'another location')}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>You can only manage units at Sharjah Yard.</div>
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Determine which buttons to show (staff roles) ──
  const canLoadContainer = (isYard || isAdmin) && ['available', 'assigned', 'paid_ready_to_ship'].includes(unit.status) && (!unitContainer || !['sealed','shipped','in_transit','at_port','delivered','closed'].includes(unitContainer?.status));
  const cutPlans = DB.get('cutPlans') || [];
  const activeCutPlan = cutPlans.find(cp => (cp.vehicleId === unit.id || cp.parentId === unit.id) && ['approved','in_progress'].includes(cp.status));
  const canCut = (isYard || isAdmin) && !!activeCutPlan;
  const canAddPhotos = isYard || isAdmin || isYardSales;
  const canUpdateLocation = isYard || isAdmin || isDestination;
  const canUpdateStatus = isYard || isAdmin;
  const canSetPrice = isAdmin || isAccountant;
  const canAssign = isAdmin;
  const canDelete = isAdmin && !(['sealed','shipped','in_transit','at_port','delivered'].includes(unitContainer?.status));
  const canViewInvoices = isAccountant;
  const canViewBreakdown = isAccountant;

  // ── Customer-specific conditions (only via QR scan) ──
  const custCanMarkArrived = isCustomer && ARRIVAL_STATUSES.includes(unit.status);
  const custCanUpdateLocation = isCustomer && ['shipped','in_transit','at_port','customs','delivered'].includes(unit.status);
  const custCanReportCondition = isCustomer && ['at_port','customs','delivered'].includes(unit.status);
  const custCanContact = isCustomer;
  const custUnitStillInJapan = isCustomer && ['available','assigned','japan_yard','loading'].includes(unit.status);

  // Destination agent conditions
  const destCanMarkArrived = isDestination && ARRIVAL_STATUSES.includes(unit.status);
  const destCanReportCondition = isDestination && ['at_port','customs','delivered'].includes(unit.status);

  // ── If a sub-action is active, render it ──
  if (action) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal qr-action-modal" onClick={e => e.stopPropagation()}>
          {action === 'load' && <LoadContainerAction unit={unit} user={user} onClose={() => { setAction(null); refreshUnit(); }} showToast={showToast} />}
          {action === 'cut' && <CutPartAction unit={unit} user={user} cutPlan={activeCutPlan} onClose={() => { setAction(null); refreshUnit(); }} showToast={showToast} />}
          {action === 'photos' && <AddPhotosAction unit={unit} onClose={() => { setAction(null); refreshUnit(); }} showToast={showToast} />}
          {action === 'location' && <UpdateLocationAction unit={unit} onClose={() => { setAction(null); refreshUnit(); }} showToast={showToast} />}
          {action === 'custLocation' && <CustomerLocationAction unit={unit} user={user} onClose={() => { setAction(null); refreshUnit(); }} showToast={showToast} />}
          {action === 'status' && <UpdateStatusAction unit={unit} user={user} isAdmin={isAdmin} onClose={() => { setAction(null); refreshUnit(); }} showToast={showToast} />}
          {action === 'price' && <SetPriceAction unit={unit} user={user} onClose={() => { setAction(null); refreshUnit(); }} showToast={showToast} />}
          {action === 'purchasePrice' && <SetPurchasePriceAction unit={unit} user={user} onClose={() => { setAction(null); refreshUnit(); }} showToast={showToast} />}
          {action === 'yardBid' && <YardBidAction unit={unit} user={user} onClose={() => { setAction(null); refreshUnit(); }} showToast={showToast} />}
          {action === 'conditionNote' && <ConditionNoteAction unit={unit} user={user} onClose={() => { setAction(null); refreshUnit(); }} showToast={showToast} />}
          {action === 'printQR' && <PrintQRAction unit={unit} onClose={() => setAction(null)} />}
          {action === 'printLabel' && <PrintLabelInline unit={unit} onClose={() => setAction(null)} />}
          {action === 'holdRequest' && <HoldRequestAction unit={unit} user={user} isAdmin={isAdmin} onClose={() => { setAction(null); refreshUnit(); }} showToast={showToast} />}
          {action === 'assign' && <AssignCustomerAction unit={unit} user={user} onClose={() => { setAction(null); refreshUnit(); }} showToast={showToast} />}
          {action === 'delete' && <DeleteUnitAction unit={unit} onClose={() => { setAction(null); onClose(); }} />}
          {action === 'arrived' && <MarkArrivedAction unit={unit} user={user} onClose={() => { setAction(null); refreshUnit(); }} showToast={showToast} />}
          {action === 'condition' && <ReportConditionAction unit={unit} user={user} onClose={() => { setAction(null); refreshUnit(); }} showToast={showToast} />}
          {action === 'contact' && <ContactAction unit={unit} user={user} onClose={() => { setAction(null); }} showToast={showToast} />}
          {action === 'breakdown' && <CostBreakdownAction unit={unit} onClose={() => setAction(null)} />}
          {action === 'invoices' && <ViewInvoicesAction unit={unit} onClose={() => setAction(null)} />}
        </div>
      </div>
    );
  }

  // ── Main action menu ──
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal qr-action-modal" onClick={e => e.stopPropagation()}>
        {toast && <div className="qr-toast">{toast}</div>}

        <div className="modal-header">
          <h3 className="modal-title">Unit Details</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body" style={{ padding: 16 }}>
          {/* ── Unit identity card ── */}
          <div className="qr-unit-card">
            <div className="qr-unit-img">
              {unit.images?.length ? <img src={unit.images[0]} alt="" /> : <span style={{ color: 'var(--text3)' }}>{getUnitIcon(unit)}</span>}
            </div>
            <div className="qr-unit-info">
              <div className="qr-unit-name">{unit.year ? `${unit.year} ` : ''}{getUnitName(unit)}</div>
              <div style={{ fontSize: 11, color: 'var(--cyan)', fontFamily: 'monospace' }}>{unit.sku}</div>
              {unit.chassis && <div style={{ fontSize: 10, color: 'var(--text3)' }}>Chassis: {unit.chassis}</div>}
              {(unit.grade || unit.mileage) && (
                <div style={{ fontSize: 10, color: 'var(--text3)' }}>
                  {unit.grade ? `Grade: ${unit.grade}` : ''}{unit.grade && unit.mileage ? ' · ' : ''}{unit.mileage ? `${Number(unit.mileage).toLocaleString()} km` : ''}
                </div>
              )}
            </div>
          </div>

          {/* Cut origin info */}
          {(unit.parent_unit_id || unit.parentId || unit.created_from_cut) && (() => {
            const allV = DB.get('vehicles') || [];
            const allP = DB.get('spareParts') || [];
            const parentId = unit.parent_unit_id || unit.parentId || unit.parent_id;
            const parent = parentId ? [...allV, ...allP].find(u => u.id === parentId) : null;
            const siblings = parentId ? allP.filter(p => p.id !== unit.id && (p.parent_unit_id === parentId || p.parentId === parentId)).slice(0, 6) : [];
            const pName = parent ? (parent.make ? `${parent.make} ${parent.model}` : parent.partName || 'Unknown') : '';
            return (
              <div style={{ padding: '8px 10px', background: 'rgba(88,166,255,0.06)', borderRadius: 8, marginBottom: 8, border: '1px solid rgba(88,166,255,0.15)' }}>
                <div style={{ fontSize: 9, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4 }}>Cut Origin</div>
                {parent && <div style={{ fontSize: 11 }}>From: <strong>{pName}</strong> <span style={{ fontFamily: 'monospace', color: 'var(--cyan)', fontSize: 9 }}>{parent.sku}</span></div>}
                {unit.cut_plan_id && <div style={{ fontSize: 10, color: 'var(--text3)' }}>Cut Plan: #{unit.cut_plan_id.slice(0, 8)}</div>}
                {unit.included_parts && Object.keys(unit.included_parts).filter(k => unit.included_parts[k]).length > 0 && (
                  <div style={{ fontSize: 10, color: 'var(--text2)', marginTop: 4 }}>
                    Includes: {Object.keys(unit.included_parts).filter(k => unit.included_parts[k]).map(k => k.replace(/_/g, ' ')).join(', ')}
                  </div>
                )}
                {siblings.length > 0 && (
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>
                    Siblings: {siblings.map(s => s.sku || s.partName).join(', ')}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Status + meta */}
          <div className="qr-meta-row">
            <div>Status: <span className={`status status-${unit.status}`}>{unit.status?.replace(/_/g, ' ')}</span></div>
            {unit.country && <div>Location: {unit.city ? `${unit.city}, ` : ''}{unit.country}</div>}
            <div>Assigned to: {custName ? <strong>{custName}</strong> : <span style={{ color: 'var(--text3)' }}>Not assigned</span>}</div>
            {unitContainer && <div>Container: <strong>{unitContainer.number || unitContainer.id}</strong></div>}
          </div>

          {/* ── QUICK ACTIONS ── */}
          <div className="qr-section-label">QUICK ACTIONS</div>
          <div className="qr-actions-list">

            {/* Yard / Admin actions */}
            {canLoadContainer && (
              <button className="qr-action-btn" onClick={() => setAction('load')}>
                <span className="qr-action-icon">📦</span>
                <div><div className="qr-action-title">Load Into Container</div><div className="qr-action-desc">Add this unit to a container</div></div>
              </button>
            )}
            {canCut && (
              <button className="qr-action-btn" onClick={() => setAction('cut')}>
                <span className="qr-action-icon">✂️</span>
                <div><div className="qr-action-title">Cut This Part</div><div className="qr-action-desc">Mark a part as extracted</div></div>
              </button>
            )}
            {(isYard || isAdmin) && !activeCutPlan && (
              <button className="qr-action-btn" disabled style={{ opacity: 0.4 }}>
                <span className="qr-action-icon">✂️</span>
                <div><div className="qr-action-title">Cut This Part</div><div className="qr-action-desc">No active cut plan</div></div>
              </button>
            )}
            {canAddPhotos && (
              <button className="qr-action-btn" onClick={() => setAction('photos')}>
                <span className="qr-action-icon">📷</span>
                <div><div className="qr-action-title">Add Photos</div><div className="qr-action-desc">Take and upload photos</div></div>
              </button>
            )}
            {(isYard || isAdmin || isAccountant) && (
              <button className="qr-action-btn" onClick={() => setAction('purchasePrice')}>
                <span className="qr-action-icon">💵</span>
                <div><div className="qr-action-title">Set Purchase Price</div><div className="qr-action-desc">Record what we paid for this unit</div></div>
              </button>
            )}
            {/* Condition Note — yard team + admin */}
            {(isYard || isAdmin || isYardSales) && (
              <button className="qr-action-btn" onClick={() => setAction('conditionNote')}>
                <span className="qr-action-icon">📝</span>
                <div><div className="qr-action-title">Add Condition Note</div><div className="qr-action-desc">Note about physical condition</div></div>
              </button>
            )}
            {/* Print QR */}
            {(isYard || isAdmin || isYardSales) && (
              <button className="qr-action-btn" onClick={() => setAction('printQR')}>
                <span className="qr-action-icon">🖨</span>
                <div><div className="qr-action-title">Print QR Sticker</div><div className="qr-action-desc">Print label for this unit</div></div>
              </button>
            )}
            {/* Print Brother Label */}
            {(isYard || isAdmin || isYardSales) && (
              <button className="qr-action-btn" onClick={() => setAction('printLabel')}>
                <span className="qr-action-icon">🏷️</span>
                <div><div className="qr-action-title">Print Label</div><div className="qr-action-desc">Brother PT-D610BT label</div></div>
              </button>
            )}
            {/* Hold Request — yard_sales requests, admin places directly */}
            {(isYardSales || isAdmin) && !['sold','sold_awaiting_payment','delivered','dismantled'].includes(unit.status) && (
              <button className="qr-action-btn" onClick={() => setAction('holdRequest')}>
                <span className="qr-action-icon">⏸</span>
                <div><div className="qr-action-title">{isAdmin ? 'Place Hold' : 'Request Hold'}</div><div className="qr-action-desc">{isAdmin ? 'Reserve this unit for a customer' : 'Ask admin to reserve this unit'}</div></div>
              </button>
            )}
            {/* Yard Bid — only for Sharjah units */}
            {(isYard || isAdmin || isYardSales) && (unit.yard_location === 'sharjah_yard') && !['sold','sold_awaiting_payment','delivered','dismantled'].includes(unit.status) && (
              <button className="qr-action-btn" onClick={() => setAction('yardBid')}>
                <span className="qr-action-icon">🏷️</span>
                <div><div className="qr-action-title">Add Yard Bid</div><div className="qr-action-desc">Record a customer's price offer</div></div>
              </button>
            )}
            {(isYard || isAdmin || isYardSales) && unit.yard_location && unit.yard_location !== 'sharjah_yard' && (
              <button className="qr-action-btn" disabled style={{ opacity: 0.4 }}>
                <span className="qr-action-icon">🏷️</span>
                <div><div className="qr-action-title">Add Yard Bid</div><div className="qr-action-desc">Only for Sharjah yard units</div></div>
              </button>
            )}
            {canUpdateLocation && (
              <button className="qr-action-btn" onClick={() => setAction('location')}>
                <span className="qr-action-icon">📍</span>
                <div><div className="qr-action-title">Update Location</div><div className="qr-action-desc">Change where this unit is</div></div>
              </button>
            )}
            {canUpdateStatus && (
              <button className="qr-action-btn" onClick={() => setAction('status')}>
                <span className="qr-action-icon">🔄</span>
                <div><div className="qr-action-title">{isAdmin ? 'Change Status' : 'Update Status'}</div><div className="qr-action-desc">{isAdmin ? 'Full status control' : 'Change unit status'}</div></div>
              </button>
            )}

            {/* ── Admin management section ── */}
            {isAdmin && (
              <>
                <div className="qr-section-label" style={{ marginTop: 8 }}>MANAGEMENT</div>
                {canSetPrice && (
                  <button className="qr-action-btn" onClick={() => setAction('price')}>
                    <span className="qr-action-icon">💰</span>
                    <div><div className="qr-action-title">Set / Update Price</div><div className="qr-action-desc">Set FOB price and cost breakdown</div></div>
                  </button>
                )}
                {canAssign && (
                  <button className="qr-action-btn" onClick={() => setAction('assign')}>
                    <span className="qr-action-icon">👤</span>
                    <div><div className="qr-action-title">Assign to Customer</div><div className="qr-action-desc">{custName ? `Currently: ${custName}` : 'Assign this unit to a customer'}</div></div>
                  </button>
                )}
              </>
            )}

            {/* ── Accountant actions ── */}
            {isAccountant && !isAdmin && (
              <>
                <button className="qr-action-btn" onClick={() => setAction('price')}>
                  <span className="qr-action-icon">💰</span>
                  <div><div className="qr-action-title">Update Pricing</div><div className="qr-action-desc">Full cost breakdown + FOB pricing</div></div>
                </button>
                {canViewBreakdown && (
                  <button className="qr-action-btn" onClick={() => setAction('breakdown')}>
                    <span className="qr-action-icon">📊</span>
                    <div><div className="qr-action-title">View Cost Breakdown</div><div className="qr-action-desc">See current pricing details</div></div>
                  </button>
                )}
                {canViewInvoices && (
                  <button className="qr-action-btn" onClick={() => setAction('invoices')}>
                    <span className="qr-action-icon">🧾</span>
                    <div><div className="qr-action-title">View Invoices</div><div className="qr-action-desc">Invoices containing this unit</div></div>
                  </button>
                )}
              </>
            )}

            {/* ── Customer actions (QR scan only) ── */}
            {isCustomer && custUnitStillInJapan && (
              <div style={{ padding: '12px 0', fontSize: 12, color: 'var(--text3)', textAlign: 'center' }}>
                This unit has not been shipped yet. 🇯🇵
              </div>
            )}
            {custCanMarkArrived && (
              <button className="qr-action-btn" onClick={() => setAction('arrived')}>
                <span className="qr-action-icon">✅</span>
                <div><div className="qr-action-title">Mark as Arrived</div><div className="qr-action-desc">Confirm this unit has reached you</div></div>
              </button>
            )}
            {custCanUpdateLocation && (
              <button className="qr-action-btn" onClick={() => setAction('custLocation')}>
                <span className="qr-action-icon">📍</span>
                <div><div className="qr-action-title">Update Location</div><div className="qr-action-desc">Set where this unit is right now</div></div>
              </button>
            )}
            {custCanReportCondition && (
              <button className="qr-action-btn" onClick={() => setAction('condition')}>
                <span className="qr-action-icon">📸</span>
                <div><div className="qr-action-title">Report Condition</div><div className="qr-action-desc">Report damage, issues, or confirm good</div></div>
              </button>
            )}
            {custCanContact && (
              <button className="qr-action-btn" onClick={() => setAction('contact')}>
                <span className="qr-action-icon">💬</span>
                <div><div className="qr-action-title">Contact Us About This Unit</div><div className="qr-action-desc">Send us a message</div></div>
              </button>
            )}

            {/* ── Destination agent actions ── */}
            {destCanMarkArrived && (
              <button className="qr-action-btn" onClick={() => setAction('arrived')}>
                <span className="qr-action-icon">✅</span>
                <div><div className="qr-action-title">Mark as Arrived</div><div className="qr-action-desc">Confirm arrival at destination</div></div>
              </button>
            )}
            {destCanReportCondition && (
              <button className="qr-action-btn" onClick={() => setAction('condition')}>
                <span className="qr-action-icon">📸</span>
                <div><div className="qr-action-title">Report Condition</div><div className="qr-action-desc">Document condition on arrival</div></div>
              </button>
            )}
            {isDestination && (
              <button className="qr-action-btn" onClick={() => setAction('location')}>
                <span className="qr-action-icon">📍</span>
                <div><div className="qr-action-title">Update Location</div><div className="qr-action-desc">Set specific destination location</div></div>
              </button>
            )}

            {/* ── Always visible ── */}
            <button className="qr-action-btn" onClick={() => { onNavigate?.('details', unit); onClose(); }}>
              <span className="qr-action-icon">📋</span>
              <div><div className="qr-action-title">View Full Details</div><div className="qr-action-desc">Open unit detail page</div></div>
            </button>

            {/* Delete (admin only, conditionally) */}
            {canDelete && (
              <button className="qr-action-btn qr-action-danger" onClick={() => setAction('delete')}>
                <span className="qr-action-icon">🗑</span>
                <div><div className="qr-action-title">Delete Unit</div><div className="qr-action-desc">Permanently remove this unit</div></div>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// SUB-ACTION COMPONENTS
// ══════════════════════════════════════════════════════════════

// ── Load into Container ──────────────────────────────────────
function LoadContainerAction({ unit, user, onClose, showToast }) {
  const containers = (DB.get('containers') || []).filter(c => ['planning','booked','loading'].includes(c.status));
  const unitContainer = (DB.get('containers') || []).find(c => (c.units || []).includes(unit.id));

  if (unitContainer) {
    return (
      <div className="modal-body" style={{ padding: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>This unit is already in a container</div>
        <div style={{ padding: 12, background: 'var(--bg3)', borderRadius: 8, marginBottom: 16 }}>
          <div style={{ fontWeight: 700 }}>{unitContainer.number || 'No number'}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{unitContainer.destination} · {unitContainer.status}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-danger" onClick={() => {
            const upd = { ...unitContainer, units: (unitContainer.units || []).filter(id => id !== unit.id), unitsData: (unitContainer.unitsData || []).filter(d => d.unit_id !== unit.id) };
            const all = DB.get('containers') || [];
            DB.set('containers', all.map(c => c.id === unitContainer.id ? upd : c));
            showToast('Removed from container');
            onClose();
          }}>Remove From Container</button>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  const allUnits = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];

  return (
    <div className="modal-body" style={{ padding: 16 }}>
      <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
        <h3 className="modal-title">Load Into Container</h3>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      {containers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 20, color: 'var(--text3)', fontSize: 13 }}>No containers available for loading</div>
      ) : (
        <div style={{ maxHeight: 360, overflow: 'auto' }}>
          {containers.map(c => {
            const unitCount = (c.units || []).length;
            return (
              <button key={c.id} className="qr-action-btn" style={{ marginBottom: 6 }} onClick={() => {
                const updatedUnits = [...(c.units || []), unit.id];
                const updatedData = [...(c.unitsData || []), { unit_id: unit.id, loaded_by: user.id, loaded_at: new Date().toISOString() }];
                const upd = { ...c, units: updatedUnits, unitsData: updatedData, status: c.status === 'planning' ? 'loading' : c.status };
                const all = DB.get('containers') || [];
                DB.set('containers', all.map(ct => ct.id === c.id ? upd : ct));
                centralUpdateStatus(unit.id, detectUnitType(unit), 'loading', { containerId: container?.id, updatedBy: user.name });
                showToast(`✅ Added to container ${c.number || c.id}`);
                onClose();
              }}>
                <span className="qr-action-icon"><ContainerIcon /></span>
                <div>
                  <div className="qr-action-title">{c.number || 'No number yet'}</div>
                  <div className="qr-action-desc">{c.destination || 'No destination'} · {unitCount} units · {c.status}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Cut Part Action ──────────────────────────────────────────
function CutPartAction({ unit, user, cutPlan, onClose, showToast }) {
  const [selectedPart, setSelectedPart] = useState(null);
  const [cutPhotos, setCutPhotos]       = useState([]);
  const [cutCondition, setCutCondition] = useState('');
  const [cutNotes, setCutNotes]         = useState('');
  const [uploading, setUploading]       = useState(false);
  const [result, setResult]             = useState(null); // { sku, qrUrl, partName, assignedTo }
  const fileRef = useRef();

  if (!cutPlan) return <div className="modal-body" style={{ padding: 20, textAlign: 'center', color: 'var(--text3)' }}>No active cut plan</div>;

  const parts = cutPlan.parts || [];
  const pendingParts = parts.filter(p => p.status !== 'completed');
  const completedParts = parts.filter(p => p.status === 'completed');
  const customers = DB.get('customers') || [];

  const handlePhotoUpload = async (e) => {
    setUploading(true);
    for (const file of e.target.files) {
      const url = await compressAndUpload(file);
      if (url) setCutPhotos(prev => [...prev, url]);
    }
    setUploading(false);
    e.target.value = '';
  };

  const markAsCut = async () => {
    if (!selectedPart || cutPhotos.length < 2 || !cutCondition) return;

    // Generate new SKU
    const isEngine = selectedPart.category === 'Engine & Transmission' || selectedPart.part_name?.toLowerCase().includes('engine');
    const dbKey = isEngine ? 'engines' : 'spareParts';
    const prefix = isEngine ? 'ENG' : 'PRT';
    const makeCode = (unit.make || 'UNK').substring(0, 3).toUpperCase();
    const d = new Date();
    const dateCode = `${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, '0')}`;
    const existing = (DB.get(dbKey) || []).filter(u => u.sku?.startsWith(`${prefix}-${makeCode}-${dateCode}`));
    const newSku = `${prefix}-${makeCode}-${dateCode}-${String(existing.length + 1).padStart(3, '0')}`;
    const newId = DB.genId(prefix);

    // Generate QR code
    let qrUrl = '';
    try {
      const qrDataUrl = await generateBrandedQR(`${window.location.origin}/?u=${newSku}`, { size: 472 });
      qrUrl = await uploadImage(qrDataUrl);
    } catch {}

    // Create the new part/engine
    const newItem = {
      id: newId,
      sku: newSku,
      ...(isEngine ? {
        engineName: selectedPart.part_name || 'Engine',
        manufacturer: unit.make || '',
      } : {
        partName: selectedPart.part_name || 'Part',
        category: selectedPart.category || '',
      }),
      make: unit.make || '',
      model: unit.model || '',
      year: unit.year || '',
      parent_type: getDbKey(unit) === 'vehicles' ? 'vehicle' : 'spare_part',
      parent_id: unit.id,
      cut_by: user.id,
      cut_at: new Date().toISOString(),
      cut_photos: cutPhotos,
      condition: cutCondition,
      condition_notes: cutNotes,
      qr_code_url: qrUrl,
      images: cutPhotos,
      status: selectedPart.assignedTo ? 'assigned' : 'available',
      assignedTo: selectedPart.assignedTo || null,
      createdAt: new Date().toISOString().split('T')[0],
    };

    const list = DB.get(dbKey) || [];
    DB.set(dbKey, [...list, newItem]);

    // Update cut plan
    const plans = DB.get('cutPlans') || [];
    const updatedParts = parts.map(p =>
      p.part_name === selectedPart.part_name && p.status !== 'completed'
        ? { ...p, status: 'completed', created_id: newId, created_sku: newSku, completed_at: new Date().toISOString(), completed_by: user.id }
        : p
    );
    const allCompleted = updatedParts.every(p => p.status === 'completed');
    const updatedPlan = {
      ...cutPlan,
      parts: updatedParts,
      status: allCompleted ? 'completed' : 'in_progress',
      actual_parts_created: [...(cutPlan.actual_parts_created || []), newId],
    };
    DB.set('cutPlans', plans.map(cp => cp.id === cutPlan.id ? updatedPlan : cp));

    // If all completed, mark source as dismantled
    if (allCompleted) {
      centralUpdateStatus(unit.id, detectUnitType(unit), 'dismantled', { updatedBy: user.name, extraFields: { dismantled: true, child_part_ids: updatedPlan.actual_parts_created } });
      Notifications.add('request_updated', 'Cut Plan Complete', `Cut plan for ${getUnitName(unit)} completed — all parts extracted`, {});
    }

    // Notify customer if assigned
    if (selectedPart.assignedTo) {
      const cust = customers.find(c => c.id === selectedPart.assignedTo);
      if (cust) {
        Notifications.add('unit_assigned', 'Part Ready', `Your part ${selectedPart.part_name} from ${getUnitName(unit)} is ready — ${newSku}`, {}, cust.id);
      }
    }

    const assignedCustName = selectedPart.assignedTo ? customers.find(c => c.id === selectedPart.assignedTo)?.name : null;
    setResult({ sku: newSku, qrUrl, partName: selectedPart.part_name, assignedTo: assignedCustName });
  };

  // ── Show result after cut ──
  if (result) {
    return (
      <div className="modal-body" style={{ padding: 20, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Part Created!</div>
        <div style={{ fontSize: 12, color: 'var(--cyan)', fontFamily: 'monospace', marginBottom: 4 }}>{result.sku}</div>
        <div style={{ fontSize: 13, marginBottom: 4 }}>{result.partName}</div>
        {result.assignedTo && <div style={{ fontSize: 12, color: 'var(--blue)' }}>→ Assigned to: {result.assignedTo}</div>}

        {result.qrUrl && (
          <div style={{ margin: '16px auto', width: 180 }}>
            <img src={result.qrUrl} alt="QR Code" style={{ width: '100%', borderRadius: 8 }} />
          </div>
        )}
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 16 }}>Print this QR code and stick it on the part.</div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          {result.qrUrl && (
            <a href={result.qrUrl} download={`QR-${result.sku}.png`} className="btn btn-secondary btn-sm">📥 Save QR Image</a>
          )}
          <button className="btn btn-success" onClick={() => { setResult(null); setSelectedPart(null); setCutPhotos([]); setCutCondition(''); setCutNotes(''); }}>
            ✅ Done — Next Part
          </button>
        </div>
      </div>
    );
  }

  // ── Part selection ──
  if (!selectedPart) {
    return (
      <div className="modal-body" style={{ padding: 16 }}>
        <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
          <h3 className="modal-title">Cut Plan — {getUnitName(unit)}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>Select which part you just extracted:</div>

        {pendingParts.map((p, i) => {
          const custN = p.assignedTo ? customers.find(c => c.id === p.assignedTo)?.name : 'Stock';
          return (
            <button key={i} className="qr-action-btn" style={{ marginBottom: 6 }} onClick={() => setSelectedPart(p)}>
              <span className="qr-action-icon">⏳</span>
              <div>
                <div className="qr-action-title">{p.part_name}</div>
                <div className="qr-action-desc">→ {custN || 'Stock'}</div>
              </div>
            </button>
          );
        })}

        {completedParts.length > 0 && (
          <>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 12, marginBottom: 6 }}>Already completed:</div>
            {completedParts.map((p, i) => (
              <div key={i} style={{ fontSize: 12, color: 'var(--green)', padding: '4px 0' }}>✅ {p.part_name} — {p.created_sku || ''}</div>
            ))}
          </>
        )}
        <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={onClose}>← Back</button>
      </div>
    );
  }

  // ── Cutting form ──
  const canSubmit = cutPhotos.length >= 2 && cutCondition && !uploading;
  const custN = selectedPart.assignedTo ? customers.find(c => c.id === selectedPart.assignedTo)?.name : null;

  return (
    <div className="modal-body" style={{ padding: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Cutting: {selectedPart.part_name}</div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>From: {getUnitName(unit)}</div>
      {custN && <div style={{ fontSize: 11, color: 'var(--blue)', marginBottom: 12 }}>For: {custN}</div>}

      {/* Photos */}
      <div style={{ marginBottom: 12 }}>
        <label className="form-label" style={{ fontSize: 11 }}>Photos (minimum 2 required)</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          {cutPhotos.map((url, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <img src={url} alt="" style={{ width: 64, height: 48, borderRadius: 6, objectFit: 'cover' }} />
              <button onClick={() => setCutPhotos(prev => prev.filter((_, idx) => idx !== i))}
                style={{ position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: '50%', background: 'var(--red)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer' }}>×</button>
            </div>
          ))}
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()}>
          📸 {cutPhotos.length === 0 ? 'Open Camera' : 'Add More'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple style={{ display: 'none' }} onChange={handlePhotoUpload} />
        {uploading && <div style={{ fontSize: 11, color: 'var(--blue)', marginTop: 4 }}>Uploading...</div>}
        <div style={{ fontSize: 10, color: cutPhotos.length >= 2 ? 'var(--green)' : 'var(--text3)', marginTop: 4 }}>
          Photos taken: {cutPhotos.length}/2 minimum
        </div>
      </div>

      {/* Condition */}
      <div style={{ marginBottom: 12 }}>
        <label className="form-label" style={{ fontSize: 11 }}>Condition</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {['Excellent', 'Good', 'Fair', 'Poor'].map(c => (
            <button key={c} className={`btn btn-sm ${cutCondition === c ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '10px 8px', fontSize: 12 }}
              onClick={() => setCutCondition(c)}>{c}</button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div className="form-group" style={{ marginBottom: 12 }}>
        <label className="form-label" style={{ fontSize: 11 }}>Notes (optional)</label>
        <textarea className="form-input" rows={2} value={cutNotes} onChange={e => setCutNotes(e.target.value)} placeholder="Any notes about the part..." style={{ resize: 'vertical', fontSize: 12 }} />
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-secondary" onClick={() => { setSelectedPart(null); setCutPhotos([]); setCutCondition(''); setCutNotes(''); }}>← Back</button>
        <button className="btn btn-success" style={{ flex: 1 }} disabled={!canSubmit} onClick={markAsCut}>
          <CheckIcon /> Mark As Cut
        </button>
      </div>
    </div>
  );
}

// ── Add Photos ───────────────────────────────────────────────
function AddPhotosAction({ unit, onClose, showToast }) {
  const [photos, setPhotos] = useState([...(unit.images || [])]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const handleUpload = async (e) => {
    setUploading(true);
    for (const file of e.target.files) {
      const url = await compressAndUpload(file);
      if (url) setPhotos(prev => [...prev, url]);
    }
    setUploading(false);
    e.target.value = '';
  };

  const save = () => {
    saveUnit(unit, { images: photos });
    showToast('✅ Photos updated');
    onClose();
  };

  return (
    <div className="modal-body" style={{ padding: 16 }}>
      <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
        <h3 className="modal-title">Add Photos — {getUnitName(unit)}</h3>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {photos.map((url, i) => (
          <div key={i} style={{ position: 'relative' }}>
            <img src={url} alt="" style={{ width: 80, height: 60, borderRadius: 6, objectFit: 'cover' }} />
            <button onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}
              style={{ position: 'absolute', top: -4, right: -4, width: 18, height: 18, borderRadius: '50%', background: 'var(--red)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer' }}>×</button>
          </div>
        ))}
      </div>
      <button className="btn btn-secondary btn-sm" style={{ marginBottom: 12 }} onClick={() => fileRef.current?.click()}>📸 Take Photos</button>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple style={{ display: 'none' }} onChange={handleUpload} />
      {uploading && <div style={{ fontSize: 11, color: 'var(--blue)', marginBottom: 8 }}>Uploading...</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-success" style={{ flex: 1 }} onClick={save}>Done</button>
      </div>
    </div>
  );
}

// ── Update Location ──────────────────────────────────────────
function UpdateLocationAction({ unit, onClose, showToast }) {
  const [custom, setCustom] = useState('');

  const setLocation = (loc) => {
    // Set yard_location if it's a yard
    const yardId = loc === 'Japan Yard' ? 'japan_yard' : loc === 'Sharjah Yard' ? 'sharjah_yard' : undefined;
    const country = loc.includes('Sharjah') ? 'UAE' : 'Japan';
    saveUnit(unit, { city: loc, country, ...(yardId ? { yard_location: yardId } : {}) });
    showToast(`✅ Location updated to ${loc}`);
    onClose();
  };

  return (
    <div className="modal-body" style={{ padding: 16 }}>
      <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
        <h3 className="modal-title">Update Location</h3>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>Yard Locations:</div>
      {YARD_LOCATIONS.map(loc => (
        <button key={loc} className="qr-action-btn" style={{ marginBottom: 4 }} onClick={() => setLocation(loc)}>
          <span className="qr-action-icon">📍</span>
          <div><div className="qr-action-title">{loc}</div></div>
        </button>
      ))}
      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 10, marginBottom: 6 }}>Or type custom:</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input className="form-input" value={custom} onChange={e => setCustom(e.target.value)} placeholder="e.g. Mombasa Port Gate 4" style={{ flex: 1, fontSize: 12 }} />
        <button className="btn btn-primary btn-sm" disabled={!custom.trim()} onClick={() => setLocation(custom.trim())}>Save</button>
      </div>
      <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={onClose}>Cancel</button>
    </div>
  );
}

// ── Customer Update Location (city + country) ───────────────
function CustomerLocationAction({ unit, user, onClose, showToast }) {
  const customers = DB.get('customers') || [];
  const customer = customers.find(c => c.id === user.id);
  const [city, setCity] = useState(unit.city || '');
  const [specificLoc, setSpecificLoc] = useState('');
  const country = customer?.country || unit.country || '';

  const handleUpdate = () => {
    if (!city.trim()) return;
    const loc = specificLoc.trim() ? `${city.trim()} — ${specificLoc.trim()}` : city.trim();
    saveUnit(unit, { city: loc, country: country });
    Notifications.add('container_arrived', 'Location Updated', `${user.name} updated location of ${getUnitName(unit)} (${unit.sku}) to ${loc}, ${country}`, {});
    showToast(`✅ Location updated to ${loc}`);
    onClose();
  };

  return (
    <div className="modal-body" style={{ padding: 16 }}>
      <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
        <h3 className="modal-title">Update Location</h3>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      <div style={{ fontSize: 13, marginBottom: 4 }}>Unit: <strong>{getUnitName(unit)}</strong></div>
      {unit.city && <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12 }}>Current location: {unit.city}{unit.country ? `, ${unit.country}` : ''}</div>}

      <div className="form-group" style={{ marginBottom: 10 }}>
        <label className="form-label">City</label>
        <input className="form-input" value={city} onChange={e => setCity(e.target.value)} placeholder="e.g. Mombasa, Dubai, Tbilisi" />
      </div>
      <div className="form-group" style={{ marginBottom: 10 }}>
        <label className="form-label">Country</label>
        <input className="form-input" value={country} disabled style={{ opacity: 0.6 }} />
      </div>
      <div className="form-group" style={{ marginBottom: 14 }}>
        <label className="form-label">Specific location (optional)</label>
        <input className="form-input" value={specificLoc} onChange={e => setSpecificLoc(e.target.value)} placeholder="e.g. My warehouse, Industrial Area" />
      </div>

      <button className="btn btn-success" style={{ width: '100%' }} disabled={!city.trim()} onClick={handleUpdate}>Update Location</button>
    </div>
  );
}

// ── Update Status ────────────────────────────────────────────
function UpdateStatusAction({ unit, user, isAdmin, onClose, showToast }) {
  const transitions = isAdmin ? ALL_STATUSES : (YARD_STATUS_TRANSITIONS[unit.status] || []);
  const [confirm, setConfirm] = useState(null);

  const applyStatus = (newStatus) => {
    centralUpdateStatus(unit.id, detectUnitType(unit), newStatus, { updatedBy: user.name });
    showToast(`✅ Status changed to ${newStatus.replace(/_/g, ' ')}`);
    onClose();
  };

  if (confirm) {
    return (
      <div className="modal-body" style={{ padding: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Confirm Status Change</div>
        <div style={{ fontSize: 12, color: 'var(--yellow)', marginBottom: 16 }}>
          This unit is currently <strong>{unit.status?.replace(/_/g, ' ')}</strong>. Changing directly to <strong>{confirm.replace(/_/g, ' ')}</strong> is unusual. Continue?
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => setConfirm(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={() => applyStatus(confirm)}>Continue</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-body" style={{ padding: 16 }}>
      <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
        <h3 className="modal-title">{isAdmin ? 'Change Status' : 'Update Status'}</h3>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>Current: <span className={`status status-${unit.status}`}>{unit.status?.replace(/_/g, ' ')}</span></div>
      <div style={{ display: 'grid', gridTemplateColumns: isAdmin ? '1fr 1fr' : '1fr', gap: 6 }}>
        {transitions.filter(s => s !== unit.status).map(s => {
          // Warn on unusual transitions for admin
          const unusual = isAdmin && !['japan_yard','available','assigned','loading','loaded','shipped','in_transit','at_port','customs','delivered','sold','cutting','dismantled'].includes(unit.status);
          return (
            <button key={s} className="btn btn-secondary btn-sm" style={{ textAlign: 'left', padding: '8px 12px' }}
              onClick={() => {
                if (isAdmin && Math.abs(ALL_STATUSES.indexOf(s) - ALL_STATUSES.indexOf(unit.status)) > 2) {
                  setConfirm(s);
                } else {
                  applyStatus(s);
                }
              }}>
              {s.replace(/_/g, ' ')}
            </button>
          );
        })}
      </div>
      <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={onClose}>Cancel</button>
    </div>
  );
}

// ── Set/Update Price ─────────────────────────────────────────
function SetPriceAction({ unit, user, onClose, showToast }) {
  const [fobPrice, setFobPrice] = useState(unit.pricing?.fob_price_usd || '');

  const quickSave = () => {
    const fob = Number(fobPrice) || 0;
    saveUnit(unit, { pricing: { ...(unit.pricing || {}), fob_price_usd: fob } });
    logActivity.priceUpdated(getUnitName(unit), user.name);
    showToast('✅ Price saved');
    onClose();
  };

  return (
    <div className="modal-body" style={{ padding: 16 }}>
      <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
        <h3 className="modal-title">Set / Update Price</h3>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      <div className="form-group" style={{ marginBottom: 12 }}>
        <label className="form-label">FOB Price (USD)</label>
        <input type="number" className="form-input" value={fobPrice} onChange={e => setFobPrice(e.target.value)} placeholder="0" style={{ fontSize: 16, fontWeight: 700 }} />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-success" style={{ flex: 1 }} onClick={quickSave}><CheckIcon /> Save</button>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

// ── Set Purchase Price ───────────────────────────────────────
function SetPurchasePriceAction({ unit, user, onClose, showToast }) {
  const [amount, setAmount] = useState(unit.costJPY || unit.pricing?.unit_price_jpy || '');
  const [currency, setCurrency] = useState('JPY');
  const [source, setSource] = useState(unit.purchase_source || 'auction');
  const [supplier, setSupplier] = useState(unit.purchase_supplier || '');
  const [notes, setNotes] = useState('');

  const SOURCES = ['Auction', 'Supplier', 'Direct Purchase', 'Other'];

  const handleSave = () => {
    const amt = Number(amount) || 0;
    if (!amt) return;
    const jpyAmount = Math.round(convertCurrency(amt, currency, 'JPY'));

    saveUnit(unit, {
      costJPY: jpyAmount,
      purchase_price: amt,
      purchase_currency: currency,
      purchase_source: source.toLowerCase(),
      purchase_supplier: supplier.trim(),
      purchase_notes: notes.trim(),
      pricing: {
        ...(unit.pricing || {}),
        unit_price_jpy: jpyAmount,
        purchase_price: amt,
        purchase_currency: currency,
      },
    });
    const sym = CURRENCIES[currency]?.symbol || currency;
    showToast(`✅ Purchase price saved: ${sym}${amt.toLocaleString()}`);
    onClose();
  };

  const curSymbol = CURRENCIES[currency]?.symbol || currency;

  return (
    <div className="modal-body" style={{ padding: 16 }}>
      <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
        <h3 className="modal-title">Set Purchase Price</h3>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      <div style={{ fontSize: 13, marginBottom: 12 }}>Unit: <strong>{getUnitName(unit)}</strong></div>

      <div className="form-group" style={{ marginBottom: 10 }}>
        <label className="form-label">How much did we pay?</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <span style={{ fontSize: 14, color: 'var(--text3)', marginRight: 6 }}>{curSymbol}</span>
            <input type="number" className="form-input" value={amount} onChange={e => setAmount(e.target.value)}
              placeholder="0" style={{ fontSize: 16, fontWeight: 700 }} />
          </div>
          <CurrencySelect value={currency} onChange={setCurrency} style={{ width: 90 }} />
        </div>
      </div>

      <div className="form-group" style={{ marginBottom: 10 }}>
        <label className="form-label">Source</label>
        <select className="form-select" value={source} onChange={e => setSource(e.target.value)}>
          {SOURCES.map(s => <option key={s} value={s.toLowerCase()}>{s}</option>)}
        </select>
      </div>

      <div className="form-group" style={{ marginBottom: 10 }}>
        <label className="form-label">Supplier / Auction House (optional)</label>
        <input className="form-input" value={supplier} onChange={e => setSupplier(e.target.value)}
          placeholder="e.g. USS Tokyo, Al Futtaim" style={{ fontSize: 12 }} />
      </div>

      <div className="form-group" style={{ marginBottom: 14 }}>
        <label className="form-label">Notes (optional)</label>
        <input className="form-input" value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="e.g. Lot #12345" style={{ fontSize: 12 }} />
      </div>

      <button className="btn btn-success" style={{ width: '100%' }} disabled={!Number(amount)} onClick={handleSave}>
        <CheckIcon /> Save Purchase Price
      </button>
    </div>
  );
}

// ── Assign to Customer ───────────────────────────────────────
function AssignCustomerAction({ unit, user, onClose, showToast }) {
  const customers = DB.get('customers') || [];
  const [search, setSearch] = useState('');
  const filtered = customers.filter(c => !search || c.name?.toLowerCase().includes(search.toLowerCase()));

  if (unit.assignedTo) {
    const current = customers.find(c => c.id === unit.assignedTo);
    return (
      <div className="modal-body" style={{ padding: 16 }}>
        <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
          <h3 className="modal-title">Assign to Customer</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div style={{ fontSize: 13, marginBottom: 12 }}>Currently assigned to: <strong>{current?.name || 'Unknown'}</strong></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={() => {
            centralUpdateStatus(unit.id, detectUnitType(unit), 'available', { updatedBy: user.name });
            showToast('Unassigned');
            onClose();
          }}>Unassign</button>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-body" style={{ padding: 16 }}>
      <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
        <h3 className="modal-title">Assign to Customer</h3>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      <input className="form-input" placeholder="Search customers..." value={search} onChange={e => setSearch(e.target.value)} style={{ marginBottom: 10, fontSize: 12 }} />
      <div style={{ maxHeight: 300, overflow: 'auto' }}>
        {filtered.map(c => (
          <button key={c.id} className="qr-action-btn" style={{ marginBottom: 4 }} onClick={() => {
            centralUpdateStatus(unit.id, detectUnitType(unit), 'assigned', { assignedTo: c.id, updatedBy: user.name });
            logActivity.unitAssigned(getUnitName(unit), c.name, user.name, c.id);
            showToast(`✅ Assigned to ${c.name}`);
            onClose();
          }}>
            <span className="qr-action-icon">👤</span>
            <div>
              <div className="qr-action-title">{c.name}</div>
              <div className="qr-action-desc">{c.country || ''}{c.city ? `, ${c.city}` : ''}</div>
            </div>
          </button>
        ))}
      </div>
      <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={onClose}>Cancel</button>
    </div>
  );
}

// ── Delete Unit ──────────────────────────────────────────────
function DeleteUnitAction({ unit, onClose }) {
  const [step, setStep] = useState(1);
  const [typed, setTyped] = useState('');

  const doDelete = () => {
    const key = getDbKey(unit);
    const list = DB.get(key) || [];
    DB.set(key, list.filter(u => u.id !== unit.id));
    // Delete from Supabase so it doesn't come back on sync
    DB.deleteRecord(key, unit.id);
    if (unit._supaId) DB.deleteRecord(key, unit._supaId);
    // Remove from containers
    const containers = DB.get('containers') || [];
    DB.set('containers', containers.map(c => ({
      ...c,
      units: (c.units || []).filter(id => id !== unit.id),
      unitsData: (c.unitsData || []).filter(d => d.unit_id !== unit.id),
    })));
    onClose();
  };

  if (step === 1) {
    return (
      <div className="modal-body" style={{ padding: 20, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🗑</div>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Are you sure you want to delete this unit?</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>{getUnitName(unit)} — {unit.sku}</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button className="btn btn-danger" onClick={() => setStep(2)}>Yes, Delete</button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="modal-body" style={{ padding: 20, textAlign: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--red)', marginBottom: 8 }}>This action cannot be undone</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
          The unit and all its data will be permanently removed.
        </div>
        <div style={{ fontSize: 12, marginBottom: 8 }}>Type <strong>{unit.sku}</strong> to confirm:</div>
        <input className="form-input" value={typed} onChange={e => setTyped(e.target.value)} placeholder={unit.sku} style={{ textAlign: 'center', fontFamily: 'monospace', marginBottom: 12 }} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button className="btn btn-danger" disabled={typed.toUpperCase() !== unit.sku?.toUpperCase()} onClick={doDelete}>Permanently Delete</button>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    );
  }
  return null;
}

// ── Mark as Arrived ──────────────────────────────────────────
function MarkArrivedAction({ unit, user, onClose, showToast }) {
  const [cond, setCond] = useState('good');
  const [city, setCity] = useState('');
  const [showConditionOffer, setShowConditionOffer] = useState(false);

  const customers = DB.get('customers') || [];
  const customer = customers.find(c => c.id === user.id);
  const country = customer?.country || user.country || unit.country || '';

  const handleConfirm = () => {
    const loc = city.trim() || unit.city || '';
    centralUpdateStatus(unit.id, detectUnitType(unit), 'delivered', { country, city: loc, updatedBy: user.name });
    // Update container unitsData
    const conts = DB.get('containers') || [];
    const cont = conts.find(c => (c.units || []).includes(unit.id));
    if (cont) {
      const ud = [...(cont.unitsData || [])];
      const idx = ud.findIndex(d => d.unit_id === unit.id);
      const arrival = { received: true, received_at: new Date().toISOString(), received_by: user.id, received_by_name: user.name, condition: cond };
      if (idx >= 0) ud[idx] = { ...ud[idx], arrival_data: arrival };
      else ud.push({ unit_id: unit.id, arrival_data: arrival });
      DB.set('containers', conts.map(c => c.id === cont.id ? { ...c, unitsData: ud } : c));
    }
    Notifications.add('container_arrived', 'Unit Arrived', `${user.name} confirmed ${getUnitName(unit)} (${unit.sku}) arrived at ${loc ? loc + ', ' : ''}${country}`, {});
    showToast('✅ Unit marked as arrived!');
    setShowConditionOffer(true);
  };

  // After arrival, offer condition report
  if (showConditionOffer) {
    return (
      <div className="modal-body" style={{ padding: 20, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Unit Marked as Arrived!</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>Want to report the condition of this unit?</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button className="btn btn-primary" onClick={() => { setShowConditionOffer(false); /* parent will handle */ onClose(); setTimeout(() => {/* caller can re-open with condition action */}, 100); }}>Yes</button>
          <button className="btn btn-secondary" onClick={onClose}>No Thanks</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-body" style={{ padding: 16 }}>
      <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
        <h3 className="modal-title">Confirm Arrival</h3>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      <div style={{ fontSize: 13, marginBottom: 12 }}>Unit: <strong>{getUnitName(unit)}</strong></div>

      {/* City input */}
      <div className="form-group" style={{ marginBottom: 10 }}>
        <label className="form-label" style={{ fontSize: 11 }}>Where are you receiving this unit?</label>
        <input className="form-input" value={city} onChange={e => setCity(e.target.value)} placeholder="City name" style={{ marginBottom: 6 }} />
        <input className="form-input" value={country} disabled style={{ opacity: 0.6, fontSize: 12 }} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label className="form-label" style={{ fontSize: 11 }}>Condition (optional)</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {CONDITIONS.map(c => (
            <button key={c.id} className={`btn btn-sm ${cond === c.id ? 'btn-primary' : 'btn-secondary'}`}
              style={{ fontSize: 12, padding: '8px 12px' }} onClick={() => setCond(c.id)}>
              {c.icon} {c.label}
            </button>
          ))}
        </div>
      </div>
      <button className="btn btn-success" style={{ width: '100%' }} onClick={handleConfirm}>
        <CheckIcon /> Confirm Arrived
      </button>
    </div>
  );
}

// ── Report Condition ─────────────────────────────────────────
function ReportConditionAction({ unit, user, onClose, showToast }) {
  const [cond, setCond]         = useState('good');
  const [photos, setPhotos]     = useState([]);
  const [notes, setNotes]       = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const handleUpload = async (e) => {
    setUploading(true);
    for (const file of e.target.files) {
      const url = await compressAndUpload(file);
      if (url) setPhotos(prev => [...prev, url]);
    }
    setUploading(false);
    e.target.value = '';
  };

  const submit = () => {
    saveUnit(unit, {
      arrival_condition: cond,
      arrival_photos: photos,
      arrival_notes: notes,
    });
    if (cond !== 'good') {
      Notifications.add('container_arrived', 'DAMAGE REPORTED', `${user.name} reported ${cond.replace(/_/g, ' ')} on ${getUnitName(unit)} (${unit.sku})`, {});
    }
    showToast('✅ Condition report submitted');
    onClose();
  };

  const needPhotos = cond !== 'good';

  return (
    <div className="modal-body" style={{ padding: 16 }}>
      <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
        <h3 className="modal-title">Report Condition</h3>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      <div style={{ fontSize: 13, marginBottom: 12 }}>Unit: <strong>{getUnitName(unit)}</strong></div>

      <div style={{ marginBottom: 12 }}>
        <label className="form-label" style={{ fontSize: 11 }}>How did this unit arrive?</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {CONDITIONS.map(c => (
            <button key={c.id} className={`btn btn-sm ${cond === c.id ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '10px 8px', fontSize: 12 }} onClick={() => setCond(c.id)}>
              {c.icon} {c.label}
            </button>
          ))}
        </div>
      </div>

      {needPhotos && (
        <div style={{ marginBottom: 12 }}>
          <label className="form-label" style={{ fontSize: 11 }}>Photos (required for damage)</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
            {photos.map((url, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <img src={url} alt="" style={{ width: 64, height: 48, borderRadius: 6, objectFit: 'cover' }} />
                <button onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}
                  style={{ position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: '50%', background: 'var(--red)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer' }}>×</button>
              </div>
            ))}
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()}>📸 Take Photos</button>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple style={{ display: 'none' }} onChange={handleUpload} />
          {uploading && <div style={{ fontSize: 11, color: 'var(--blue)', marginTop: 4 }}>Uploading...</div>}
        </div>
      )}

      {needPhotos && (
        <div className="form-group" style={{ marginBottom: 12 }}>
          <label className="form-label" style={{ fontSize: 11 }}>Describe the issue</label>
          <textarea className="form-input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Describe the damage..." style={{ resize: 'vertical', fontSize: 12 }} />
        </div>
      )}

      <button className="btn btn-success" style={{ width: '100%' }} disabled={needPhotos && photos.length === 0} onClick={submit}>
        <CheckIcon /> Submit Report
      </button>
    </div>
  );
}

// ── Contact Us ───────────────────────────────────────────────
function ContactAction({ unit, user, onClose, showToast }) {
  const [message, setMessage] = useState('');

  const send = () => {
    if (!message.trim()) return;
    const requests = DB.get('requests') || [];
    const newReq = {
      id: DB.genId('REQ'),
      customerId: user.id,
      customerName: user.name,
      type: 'message',
      vehicleId: unit.id,
      vehicleSku: unit.sku,
      vehicleName: getUnitName(unit),
      details: message.trim(),
      status: 'pending',
      createdAt: new Date().toISOString().split('T')[0],
      thread: [],
    };
    DB.set('requests', [...requests, newReq]);
    Notifications.add('request_new', 'Unit Message', `${user.name} sent message about ${getUnitName(unit)} (${unit.sku}): ${message.trim().substring(0, 80)}`, {});
    showToast('✅ Message sent');
    onClose();
  };

  return (
    <div className="modal-body" style={{ padding: 16 }}>
      <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
        <h3 className="modal-title">Message About Unit</h3>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>{getUnitName(unit)} — {unit.sku}</div>
      <textarea className="form-input" rows={3} value={message} onChange={e => setMessage(e.target.value)} placeholder="Type your message..." style={{ resize: 'vertical', fontSize: 12, marginBottom: 12 }} />
      <button className="btn btn-primary" style={{ width: '100%' }} disabled={!message.trim()} onClick={send}>Send</button>
    </div>
  );
}

// ── Cost Breakdown (read-only) ───────────────────────────────
function CostBreakdownAction({ unit, onClose }) {
  const p = unit.pricing || {};
  const jpyFields = ['unit_price_jpy','recycle_fee_jpy','misc_jpy','bid_fee_jpy','transport_jpy','vanning_jpy','container_share_jpy','freight_jpy','commission_jpy'];
  const destFields = ['freight_dest','clearing_exp','yard_charges','local_transport','other_charges'];
  const totalJpy = jpyFields.reduce((s, k) => s + (Number(p[k]) || 0), 0);
  const totalDest = destFields.reduce((s, k) => s + (Number(p[k]) || 0), 0);
  const rate = 149.5;
  const costUsd = totalJpy / rate;
  const fob = p.fob_price_usd || 0;
  const margin = fob - (costUsd + totalDest);

  return (
    <div className="modal-body" style={{ padding: 16 }}>
      <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
        <h3 className="modal-title">Cost Breakdown</h3>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      {Object.keys(p).length === 0 ? (
        <div style={{ color: 'var(--text3)', fontSize: 12 }}>No pricing set yet.</div>
      ) : (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Japan Costs (JPY)</div>
          {jpyFields.filter(k => Number(p[k]) > 0).map(k => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '2px 0', color: 'var(--text2)' }}>
              <span>{k.replace(/_/g, ' ').replace('jpy', '').trim()}</span>
              <span>¥{Number(p[k]).toLocaleString()}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, padding: '6px 0', borderTop: '1px solid var(--border)', marginTop: 4 }}>
            <span>Total JPY</span><span>¥{totalJpy.toLocaleString()} (${Math.round(costUsd).toLocaleString()})</span>
          </div>
          {totalDest > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, marginTop: 10, marginBottom: 6 }}>Destination (USD)</div>
              {destFields.filter(k => Number(p[k]) > 0).map(k => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '2px 0', color: 'var(--text2)' }}>
                  <span>{k.replace(/_/g, ' ')}</span><span>${Number(p[k]).toLocaleString()}</span>
                </div>
              ))}
            </>
          )}
          {fob > 0 && (
            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700 }}>
                <span>FOB Price</span><span style={{ color: 'var(--green)' }}>${fob.toLocaleString()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: margin >= 0 ? 'var(--green)' : 'var(--red)' }}>
                <span>Margin</span><span>${Math.round(margin).toLocaleString()}</span>
              </div>
            </div>
          )}
        </>
      )}
      <button className="btn btn-secondary btn-sm" style={{ marginTop: 12 }} onClick={onClose}>Close</button>
    </div>
  );
}

// ── View Invoices ────────────────────────────────────────────
function ViewInvoicesAction({ unit, onClose }) {
  const invoices = (DB.get('invoices') || []).filter(inv =>
    (Array.isArray(inv.lineItems) ? inv.lineItems : []).some(li => li.unit_id === unit.id || li.unit_sku === unit.sku)
  );

  return (
    <div className="modal-body" style={{ padding: 16 }}>
      <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
        <h3 className="modal-title">Invoices</h3>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      {invoices.length === 0 ? (
        <div style={{ color: 'var(--text3)', fontSize: 12 }}>No invoices include this unit.</div>
      ) : (
        invoices.map(inv => (
          <div key={inv.id} style={{ padding: '8px 10px', background: 'var(--bg3)', borderRadius: 6, marginBottom: 6, fontSize: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 600 }}>{inv.invoiceNumber}</span>
              <span className={`status status-${inv.status === 'paid' ? 'available' : 'pending'}`} style={{ fontSize: 9 }}>{inv.status}</span>
            </div>
            <div style={{ fontSize: 10, color: 'var(--text3)' }}>{inv.customerName} · ${(inv.total || 0).toLocaleString()}</div>
          </div>
        ))
      )}
      <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={onClose}>Close</button>
    </div>
  );
}

// ── Yard Bid (inline — quick phone + price entry) ───────────
function YardBidAction({ unit, user, onClose, showToast }) {
  const [phone, setPhone] = useState('');
  const [custName, setCustName] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(localStorage.getItem('bigstar_yard_cur') || 'AED');
  const [notes, setNotes] = useState('');

  // Find customer by phone (inline, no useEffect)
  const clean = phone.replace(/[^\d+]/g, '');
  const customers = DB.get('customers') || [];
  const foundCust = clean.length >= 6 ? customers.find(c => {
    const cp = (c.phone || '').replace(/[^\d+]/g, '');
    return cp && (cp === clean || cp.endsWith(clean.slice(-9)) || clean.endsWith(cp.slice(-9)));
  }) : null;

  const handleSubmit = () => {
    const amt = Number(amount);
    if (!amt || (!foundCust && !custName.trim()) || clean.length < 6) return;

    let customer = foundCust;
    if (!customer) {
      let p = clean;
      if (p.startsWith('05')) p = '+971' + p.substring(1);
      else if (p.startsWith('5') && p.length >= 9) p = '+971' + p;
      else if (!p.startsWith('+')) p = '+971' + p;
      customer = { id: DB.genId('C'), name: custName.trim(), phone: p, country: 'UAE', currency: 'AED', is_walk_in: true, createdAt: new Date().toISOString().split('T')[0] };
      DB.set('customers', [...customers, customer]);
    }

    const amtUsd = currency === 'USD' ? amt : Math.round(amt / 3.67);
    localStorage.setItem('bigstar_yard_cur', currency);

    const allBids = DB.get('yardBids') || [];
    const existing = allBids.find(b => b.unit_id === unit.id && b.customer_id === customer.id && b.status === 'active');
    if (existing) {
      DB.setAsync('yardBids', allBids.map(b => b.id === existing.id ? { ...b, amount: amt, currency, amount_usd: amtUsd, notes: notes.trim(), updated_at: new Date().toISOString() } : b));
    } else {
      DB.setAsync('yardBids', [...allBids, {
        id: DB.genId('BID'), unit_id: unit.id, unit_type: unit.make ? 'vehicle' : unit.engineName ? 'engine' : 'spare_part',
        unit_sku: unit.sku, unit_description: getUnitName(unit),
        customer_id: customer.id, customer_name: customer.name, customer_phone: customer.phone,
        amount: amt, currency, amount_usd: amtUsd, status: 'active',
        yard_location: 'sharjah_yard', notes: notes.trim(),
        entered_by: user.id, entered_by_name: user.name,
        expires_at: new Date(Date.now() + 86400000).toISOString(), created_at: new Date().toISOString(),
      }]);
    }

    showToast(`✅ Bid: ${currency} ${amt.toLocaleString()} from ${customer.name}`);
    onClose();
  };

  return (
    <div className="modal-body" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Add Yard Bid</h3>
        <button style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 20, cursor: 'pointer' }} onClick={onClose}>×</button>
      </div>
      <div style={{ fontSize: 13, marginBottom: 10 }}>Unit: <strong>{getUnitName(unit)}</strong></div>

      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Phone</div>
        <input className="form-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+971 55..." style={{ fontSize: 15, fontFamily: 'monospace' }} autoFocus />
        {foundCust && <div style={{ fontSize: 11, color: 'var(--green)', marginTop: 4 }}>✅ {foundCust.name}</div>}
        {!foundCust && clean.length >= 6 && (
          <input className="form-input" value={custName} onChange={e => setCustName(e.target.value)} placeholder="Customer name" style={{ marginTop: 6, fontSize: 13 }} />
        )}
      </div>

      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Price</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="number" className="form-input" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" style={{ fontSize: 18, fontWeight: 700, flex: 1 }} onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }} />
          <select className="form-select" value={currency} onChange={e => setCurrency(e.target.value)} style={{ width: 80 }}>
            <option value="AED">AED</option><option value="USD">USD</option>
          </select>
        </div>
      </div>

      <input className="form-input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" style={{ fontSize: 12, marginBottom: 12 }} />

      <button className="btn btn-success" style={{ width: '100%', padding: 14, fontSize: 15 }}
        disabled={!Number(amount) || (!foundCust && !custName.trim()) || clean.length < 6}
        onClick={handleSubmit}>
        <CheckIcon /> Submit Bid
      </button>
    </div>
  );
}

// ── Condition Note Action ────────────────────────────────────
function ConditionNoteAction({ unit, user, onClose, showToast }) {
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState([]);
  const [severity, setSeverity] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const handleUpload = async (e) => {
    setUploading(true);
    for (const f of e.target.files) { const url = await compressAndUpload(f); if (url) setPhotos(prev => [...prev, url]); }
    setUploading(false); e.target.value = '';
  };

  const handleSave = () => {
    if (!note.trim() || !severity) return;
    const key = getDbKey(unit);
    const list = DB.get(key) || [];
    const existing = Array.isArray(unit.condition_notes) ? unit.condition_notes : [];
    const entry = { id: DB.genId('CN'), note: note.trim(), photos, severity, reported_by: user.id, reported_by_name: user.name, reported_at: new Date().toISOString() };
    DB.set(key, list.map(u => u.id === unit.id ? { ...u, condition_notes: [...existing, entry] } : u));
    Notifications.add('general', 'Condition Note', `${user.name} noted on ${getUnitName(unit)}: ${note.trim().substring(0, 50)}`, {});
    showToast('✅ Note saved');
    onClose();
  };

  return (
    <div className="modal-body" style={{ padding: 16 }}>
      <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
        <h3 className="modal-title">Condition Note</h3>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      <div style={{ fontSize: 13, marginBottom: 12 }}>Unit: <strong>{getUnitName(unit)}</strong></div>
      <div className="form-group" style={{ marginBottom: 10 }}>
        <label className="form-label">What did you notice? <span className="req">*</span></label>
        <textarea className="form-input" rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Describe the condition..." style={{ resize: 'vertical' }} />
      </div>
      <div style={{ marginBottom: 10 }}>
        <label className="form-label">Photos (recommended)</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          {photos.map((url, i) => <img key={i} src={url} alt="" style={{ width: 56, height: 42, borderRadius: 4, objectFit: 'cover' }} />)}
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()}>📸 Take Photos</button>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple style={{ display: 'none' }} onChange={handleUpload} />
        {uploading && <div style={{ fontSize: 10, color: 'var(--blue)', marginTop: 4 }}>Uploading...</div>}
      </div>
      <div style={{ marginBottom: 14 }}>
        <label className="form-label">Severity <span className="req">*</span></label>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['minor', 'Minor'], ['moderate', 'Moderate'], ['major', 'Major']].map(([id, label]) => (
            <button key={id} className={`btn btn-sm ${severity === id ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1, padding: '10px 4px' }} onClick={() => setSeverity(id)}>{label}</button>
          ))}
        </div>
      </div>
      <button className="btn btn-success" style={{ width: '100%' }} disabled={!note.trim() || !severity || uploading} onClick={handleSave}>
        <CheckIcon /> Save Note
      </button>
    </div>
  );
}

// ── Print QR Action ─────────────────────────────────────────
function PrintQRAction({ unit, onClose }) {
  const [qrUrl, setQrUrl] = useState(unit.qr_code_url || '');
  const [generating, setGenerating] = useState(!unit.qr_code_url);

  useEffect(() => {
    if (!unit.qr_code_url) {
      generateBrandedQR(`${window.location.origin}/?u=${unit.sku || unit.id}`, { size: 512 }).then(dataUrl => {
        import('../data/database').then(({ uploadImage }) => {
          uploadImage(dataUrl).then(url => {
            const finalUrl = url || dataUrl;
            setQrUrl(finalUrl);
            setGenerating(false);
            const key = getDbKey(unit);
            const list = DB.get(key) || [];
            DB.set(key, list.map(u => u.id === unit.id ? { ...u, qr_code_url: finalUrl } : u));
          });
        });
      });
    }
  }, []); // eslint-disable-line

  const printSingle = (size) => {
    const px = size === 'small' ? 113 : 151;
    const html = `<!DOCTYPE html><html><head><style>@page{size:${size === 'small' ? '30mm 30mm' : '40mm 44mm'};margin:0}body{margin:0;display:flex;align-items:center;justify-content:center;height:100vh}img{width:${px}px;height:${px}px;image-rendering:pixelated}</style></head><body><div style="text-align:center"><img src="${qrUrl}"/>${size !== 'small' ? '<div style="width:6px;height:6px;border:1px solid #000;border-radius:50%;margin:3px auto 0"></div>' : ''}</div><script>window.onload=()=>window.print()<\/script></body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  return (
    <div className="modal-body" style={{ padding: 20, textAlign: 'center' }}>
      <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
        <h3 className="modal-title">Print QR Sticker</h3>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      {generating ? (
        <div style={{ padding: 20, color: 'var(--text3)' }}>Generating QR code...</div>
      ) : (
        <>
          {qrUrl && <img src={qrUrl} alt="QR" style={{ width: 180, height: 180, imageRendering: 'pixelated', borderRadius: 8 }} />}
          <div style={{ fontSize: 11, color: 'var(--cyan)', fontFamily: 'monospace', marginTop: 8 }}>{unit.sku}</div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
            <button className="btn btn-primary btn-sm" onClick={() => printSingle('small')}>Small 30×30mm</button>
            <button className="btn btn-secondary btn-sm" onClick={() => printSingle('large')}>Large 40×40mm</button>
          </div>
          {qrUrl && <a href={qrUrl} download={`QR-${unit.sku}.png`} className="btn btn-secondary btn-sm" style={{ marginTop: 8, display: 'inline-block' }}>📥 Download</a>}
        </>
      )}
    </div>
  );
}

// ── Print Label Action (Brother PT-D610BT) ─────────────────
function PrintLabelInline({ unit, onClose }) {
  return <PrintLabelModal unit={unit} onClose={onClose} />;
}

// ── Hold Request Action ─────────────────────────────────────
function HoldRequestAction({ unit, user, isAdmin, onClose, showToast }) {
  const [phone, setPhone] = useState('');
  const [custName, setCustName] = useState('');
  const [foundCust, setFoundCust] = useState(null);
  const [duration, setDuration] = useState('2h');
  const [reason, setReason] = useState('');

  useEffect(() => {
    const clean = phone.replace(/[^\d+]/g, '');
    if (clean.length >= 6) {
      const customers = DB.get('customers') || [];
      const found = customers.find(c => {
        const cp = (c.phone || '').replace(/[^\d+]/g, '');
        return cp && clean && (cp === clean || cp.endsWith(clean.slice(-9)) || clean.endsWith(cp.slice(-9)));
      });
      setFoundCust(found || null);
      if (found) setCustName(found.name);
    } else setFoundCust(null);
  }, [phone]);

  const handleSubmit = () => {
    if ((!foundCust && !custName.trim()) || phone.replace(/[^\d+]/g, '').length < 6) return;
    let customer = foundCust;
    if (!customer) {
      let p = phone.replace(/[^\d+]/g, '');
      if (p.startsWith('05')) p = '+971' + p.substring(1);
      if (p.startsWith('5') && p.length >= 9) p = '+971' + p;
      const customers = DB.get('customers') || [];
      customer = { id: DB.genId('C'), name: custName.trim(), phone: p, country: 'UAE', currency: 'AED', is_walk_in: true, createdAt: new Date().toISOString().split('T')[0] };
      DB.set('customers', [...customers, customer]);
    }
    const hours = duration === '1h' ? 1 : duration === 'tomorrow' ? 24 : 2;
    const holdUntil = new Date(Date.now() + hours * 3600000).toISOString();
    const key = getDbKey(unit);
    const list = DB.get(key) || [];

    if (isAdmin) {
      DB.set(key, list.map(u => u.id === unit.id ? { ...u, hold_for_customer_id: customer.id, hold_until: holdUntil, hold_approved_by: user.id, hold_reason: reason.trim() } : u));
      showToast(`✅ Hold placed for ${customer.name}`);
      Notifications.add('general', 'Hold Placed', `${getUnitName(unit)} held for ${customer.name}`, {});
    } else {
      DB.set(key, list.map(u => u.id === unit.id ? { ...u, hold_requested_by: user.id, hold_reason: reason.trim(), _hold_request: { customer_id: customer.id, customer_name: customer.name, duration, holdUntil, requested_by: user.name } } : u));
      showToast('Hold request sent to admin');
      Notifications.add('general', 'Hold Requested', `${user.name} requests hold: ${getUnitName(unit)} for ${customer.name}`, {});
    }
    onClose();
  };

  return (
    <div className="modal-body" style={{ padding: 16 }}>
      <div className="modal-header" style={{ padding: 0, marginBottom: 12 }}>
        <h3 className="modal-title">{isAdmin ? 'Place Hold' : 'Request Hold'}</h3>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      <div style={{ fontSize: 13, marginBottom: 12 }}>Unit: <strong>{getUnitName(unit)}</strong></div>
      <div className="form-group" style={{ marginBottom: 10 }}>
        <label className="form-label">Customer Phone</label>
        <input className="form-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+971 55..." style={{ fontFamily: 'monospace' }} />
        {foundCust && <div style={{ fontSize: 11, color: 'var(--green)', marginTop: 4 }}>✅ {foundCust.name}</div>}
        {!foundCust && phone.replace(/[^\d+]/g, '').length >= 6 && <input className="form-input" value={custName} onChange={e => setCustName(e.target.value)} placeholder="Customer name" style={{ marginTop: 6 }} />}
      </div>
      <div style={{ marginBottom: 10 }}>
        <label className="form-label">Duration</label>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['1h', '1 hour'], ['2h', '2 hours'], ['tomorrow', 'Until tomorrow']].map(([id, label]) => (
            <button key={id} className={`btn btn-sm ${duration === id ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1 }} onClick={() => setDuration(id)}>{label}</button>
          ))}
        </div>
      </div>
      <div className="form-group" style={{ marginBottom: 14 }}>
        <label className="form-label">Reason (optional)</label>
        <input className="form-input" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Customer coming back with cash" />
      </div>
      <button className="btn btn-success" style={{ width: '100%' }} disabled={(!foundCust && !custName.trim()) || phone.replace(/[^\d+]/g, '').length < 6} onClick={handleSubmit}>
        <CheckIcon /> {isAdmin ? 'Place Hold' : 'Request Hold'}
      </button>
    </div>
  );
}

export default QRActionMenu;
